import { useEffect, useState } from 'react';
import { Form } from 'react-bootstrap';
import { ProductPageEditHeader } from './ProductPageEditHeader';
import { ProductPageContent } from '@/backend/models/productPageContent';
import { useProductPageStore } from '@/frontend/store/ProductPageStore';
import { resize } from '@/frontend/utils/parseReferences';
import { I18n } from '@/i18n/I18n';
import { FileSubjectTypes } from '@/types/fileTypes';
import { useShallow } from 'zustand/react/shallow';
import { ProductPageContentType } from '@/backend/access/productPageContent/types';
import { FileDragAndDropWithDisplay } from '../files/FileDragAndDropWithDisplay';

interface ProductPageContentEditorProps {
  content: ProductPageContent;
}

export function ProductPageContentEditor({ content }: ProductPageContentEditorProps) {
  const deleteContent = useProductPageStore(state => state.deleteContent);
  const duplicateContent = useProductPageStore(state => state.duplicateContent);
  const reorderContents = useProductPageStore(state => state.orderContents);
  const siblingContents = useProductPageStore(useShallow(state => state.contents.filter(c => c.productPageSectionId === content.productPageSectionId).sort((a, b) => a.position - b.position)));

  return (
    <div className="product-page-content-card">
      <ProductPageEditHeader siblings={siblingContents} item={content} reorder={reorderContents} duplicate={duplicateContent} deleteAction={deleteContent}>
        <span className="product-page-content-type-label">{I18n.get(content.type)}</span>
      </ProductPageEditHeader>
      <ProductPageContentBody content={content} />
    </div>
  );
}

export function ProductPageContentBody({ content }: { content: ProductPageContent }) {
  if (content.type === ProductPageContentType.TEXT) {
    return <TextContentEditor content={content} />;
  }
  return <FileContentEditor content={content} />;
}

function TextContentEditor({ content }: { content: ProductPageContent }) {
  const updateContent = useProductPageStore(state => state.updateContent);
  const [description, setDescription] = useState(content.description);
  const textId = `text-content-${content.id}`;

  useEffect(() => { resize(textId); }, [textId]);

  const handleChange = (value: string) => {
    setDescription(value);
    resize(textId);
  };

  const handleBlur = () => {
    if (description !== content.description) {
      void updateContent({ ...content, description });
    }
  };

  return (
    <Form.Control
      as="textarea"
      id={textId}
      rows={1}
      value={description}
      onChange={(e) => handleChange(e.target.value)}
      onBlur={handleBlur}
      placeholder={I18n.get('addYourText')}
      className="product-page-textarea"
      aria-label={I18n.get('text')}
    />
  );
}

function FileContentEditor({ content }: { content: ProductPageContent }) {
  const updateContent = useProductPageStore(state => state.updateContent);
  const fetchContent = useProductPageStore(state => state.fetchContent);

  const [title, setTitle] = useState(content.title);
  const [description, setDescription] = useState(content.description);
  const fileDescriptionId = `file-description-${content.id}`;

  useEffect(() => { resize(fileDescriptionId); }, [fileDescriptionId]);

  const handleDescriptionChange = (value: string) => {
    setDescription(value);
    resize(fileDescriptionId);
  };

  const handleTitleBlur = () => {
    if (title !== content.title) {
      void updateContent({ ...content, title });
    }
  };

  const handleDescriptionBlur = () => {
    if (description !== content.description) {
      void updateContent({ ...content, description });
    }
  };

  return (
    <div>
      <FileDragAndDropWithDisplay file={content.file} subjectId={content.id} subjectType={FileSubjectTypes.PRODUCT_PAGE_CONTENT} refetchSubject={() => void fetchContent(content.id)} fileType={content.type} />
      <Form.Group className="mb-2">
        <Form.Label className="form-label-neutral">{I18n.get('title')}</Form.Label>
        <Form.Control
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onBlur={handleTitleBlur}
          className="form-control-neutral"
          aria-label={I18n.get('enterTitle')}
        />
      </Form.Group>

      <Form.Group>
        <Form.Label className="form-label-neutral">{I18n.get('description')}</Form.Label>
        <Form.Control
          as="textarea"
          id={fileDescriptionId}
          rows={1}
          value={description}
          onChange={(e) => handleDescriptionChange(e.target.value)}
          onBlur={handleDescriptionBlur}
          className="form-control-neutral"
          aria-label={I18n.get('enterDescription')}
        />
      </Form.Group>
    </div>
  );
}
